import {
  BlissfulIcon,
  BlossomIcon,
  Box,
  Button,
  CrossIcon,
  DismissIcon,
  Flex,
  FriendsIcon,
  HopIcon,
  Image,
  LemonIcon,
  LightbulbIcon,
  LightningIcon,
  NotHighIcon,
  PainFreeIcon,
  PencilIcon,
  PepperIcon,
  PizzaIcon,
  RelaxedIcon,
  RelaxedWaveIcon,
  SleepIcon,
  Typography,
} from '@iheartjane/reefer';
import { formatCurrency } from '@iheartjane/utils';
import capitalize from 'lodash-es/capitalize';
import type { ReactNode } from 'react';

import { RatingStars } from '../productReviews/ratingStars/ratingStars';
import { AttributePill } from './attributePill';
import type { CompareProduct } from './compareProvider';

import styles from './compareModal.module.css';

const ICON_SIZE = 'sm';

const FEELING_ICONS: Record<string, ReactNode> = {
  Blissful: <BlissfulIcon size={ICON_SIZE} />,
  Creative: <PencilIcon size={ICON_SIZE} />,
  Energetic: <LightningIcon size={ICON_SIZE} />,
  Hungry: <PizzaIcon size={ICON_SIZE} />,
  'Not high': <NotHighIcon size={ICON_SIZE} />,
  'Pain free': <PainFreeIcon size={ICON_SIZE} />,
  Relaxed: <RelaxedIcon size={ICON_SIZE} />,
  Relieved: <PainFreeIcon size={ICON_SIZE} />,
  Sleepy: <SleepIcon size={ICON_SIZE} />,
};

const ACTIVITY_ICONS: Record<string, ReactNode> = {
  'Ease my mind': <RelaxedWaveIcon size={ICON_SIZE} />,
  'Get active': <LightningIcon size={ICON_SIZE} />,
  'Get relief': <CrossIcon size={ICON_SIZE} />,
  'Get some sleep': <SleepIcon size={ICON_SIZE} />,
  'Hang with friends': <FriendsIcon size={ICON_SIZE} />,
  Relaxed: <RelaxedIcon size={ICON_SIZE} />,
  'Stimulate my mind': <LightbulbIcon size={ICON_SIZE} />,
};

const FLAVOR_ICONS: Record<string, ReactNode> = {
  Citrusy: <LemonIcon size={ICON_SIZE} />,
  Floral: <BlossomIcon size={ICON_SIZE} />,
  Hoppy: <HopIcon size={ICON_SIZE} />,
  Spicy: <PepperIcon size={ICON_SIZE} />,
};

const EmptyCell = () => (
  <Typography
    variant="body"
    color="grays-mid"
    as="p"
    aria-label="Not available"
  >
    &mdash;
  </Typography>
);

function formatPotency(
  product: CompareProduct,
  field: 'percentThc' | 'percentCbd'
): string {
  const value = product[field];
  const mg = field === 'percentThc' ? product.thcDosageMg : product.cbdDosageMg;
  const isEdible = product.kind === 'edible' || product.kind === 'tincture';
  if ((isEdible || value == null) && mg != null) return `${mg}mg`;
  if (value == null) return '';
  return `${value}%`;
}

interface CompareTableProps {
  addToCartLabel?: string;
  /**
   * Returns how many of a product are already in the bag. When > 0 the count is
   * appended to the add button (e.g. "Add (1)") so shoppers get confirmation a
   * product was added — Bloom has no slide-out bag to signal it otherwise.
   */
  getCountInCart?: (productId: number) => number;
  isMobile: boolean;
  onAddToCart: (product: CompareProduct) => void;
  onProductClick: (product: CompareProduct) => void;
  onRemove: (id: number) => void;
  products: CompareProduct[];
}

export const CompareTable = ({
  addToCartLabel = 'Add',
  getCountInCart,
  products,
  onRemove,
  onProductClick,
  onAddToCart,
  isMobile,
}: CompareTableProps) => {
  const rows: {
    label: string;
    render: (product: CompareProduct) => React.ReactNode;
  }[] = [
    {
      label: 'Price',
      render: (product) => {
        if (product.price == null) return <EmptyCell />;
        if (product.originalPrice != null) {
          return (
            <Flex justifyContent="center" gap={4} alignItems="baseline">
              <Typography
                as="p"
                variant="body-bold"
                className={styles['salePrice']}
              >
                {formatCurrency(product.price)}
              </Typography>
              <Typography as="p" variant="mini" color="grays-mid" strikeThrough>
                {formatCurrency(product.originalPrice)}
              </Typography>
            </Flex>
          );
        }
        return (
          <Typography as="p" variant="body-bold">
            {formatCurrency(product.price)}
          </Typography>
        );
      },
    },
    {
      label: 'THC',
      render: (product) => {
        const val = formatPotency(product, 'percentThc');
        return val ? (
          <Typography as="p" variant="body">
            {val}
          </Typography>
        ) : (
          <EmptyCell />
        );
      },
    },
    {
      label: 'CBD',
      render: (product) => {
        const val = formatPotency(product, 'percentCbd');
        return val ? (
          <Typography as="p" variant="body">
            {val}
          </Typography>
        ) : (
          <EmptyCell />
        );
      },
    },
    {
      label: 'Strain',
      render: (product) =>
        product.category ? (
          <Typography as="p" variant="body">
            {capitalize(product.category)}
          </Typography>
        ) : (
          <EmptyCell />
        ),
    },
    {
      label: 'Feelings',
      render: (product) =>
        product.feelings.length > 0 ? (
          <Flex flexWrap="wrap" justifyContent="center" gap={4}>
            {product.feelings.slice(0, 3).map((feeling) => (
              <AttributePill
                key={feeling}
                label={feeling}
                icon={FEELING_ICONS[feeling]}
              />
            ))}
          </Flex>
        ) : (
          <EmptyCell />
        ),
    },
    {
      label: 'Activities',
      render: (product) =>
        product.activities.length > 0 ? (
          <Flex flexWrap="wrap" justifyContent="center" gap={4}>
            {product.activities.slice(0, 3).map((activity) => (
              <AttributePill
                key={activity}
                label={activity}
                icon={ACTIVITY_ICONS[activity]}
              />
            ))}
          </Flex>
        ) : (
          <EmptyCell />
        ),
    },
    {
      label: 'Flavors',
      render: (product) =>
        product.flavors.length > 0 ? (
          <Flex flexWrap="wrap" justifyContent="center" gap={4}>
            {product.flavors.slice(0, 3).map((flavor) => (
              <AttributePill
                key={flavor}
                label={flavor}
                icon={FLAVOR_ICONS[flavor]}
              />
            ))}
          </Flex>
        ) : (
          <EmptyCell />
        ),
    },
    {
      label: 'Type',
      render: (product) =>
        product.kind ? (
          <Typography as="p" variant="body">
            {capitalize(product.kind)}
          </Typography>
        ) : (
          <EmptyCell />
        ),
    },
    {
      label: 'Brand',
      render: (product) =>
        product.brand ? (
          <Typography as="p" variant="body">
            {product.brand}
          </Typography>
        ) : (
          <EmptyCell />
        ),
    },
    {
      label: 'Rating',
      render: (product) => {
        if (product.aggregateRating == null) return <EmptyCell />;
        return (
          <Flex
            className={styles['ratingWrap']}
            alignItems="center"
            justifyContent="center"
            gap={4}
          >
            <RatingStars rating={product.aggregateRating} />
            <Typography as="p" variant="body">
              {product.aggregateRating.toFixed(1)}
              {product.reviewCount != null ? ` (${product.reviewCount})` : ''}
            </Typography>
          </Flex>
        );
      },
    },
    {
      label: 'Weight',
      render: (product) =>
        product.weight ? (
          <Typography as="p" variant="body">
            {product.weight}
          </Typography>
        ) : (
          <EmptyCell />
        ),
    },
  ];

  return (
    <table className={styles['table']}>
      <colgroup>
        <col className={styles['labelCol']} />
        {products.map((product) => (
          <col key={product.id} />
        ))}
      </colgroup>
      <thead className={styles['thead']}>
        <tr>
          <th className={styles['labelCell']} />
          {products.map((product) => (
            <th key={product.id}>
              <Box className={styles['productImg']}>
                <Image
                  src={product.imageUrl}
                  altText={product.name}
                  width="100%"
                  height="100%"
                  objectFit="cover"
                  loading="lazy"
                />
                <Button.Icon
                  className={styles['removeBtn']}
                  icon={<DismissIcon size="sm" />}
                  onClick={() => onRemove(product.id)}
                  aria-label={`Remove ${product.name}`}
                  variant="minimal"
                />
              </Box>
              <Button
                className={styles['productName']}
                onClick={() => onProductClick(product)}
                to={product.url ?? undefined}
                aria-label={`View ${product.name}`}
                variant="minimal"
                label={
                  <Typography as="p" variant="mini-bold" truncateAt="160px">
                    {product.name}
                  </Typography>
                }
              />
              {product.brand && (
                <Typography as="p" variant="mini" color="grays-mid">
                  {product.brand}
                </Typography>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            <td className={styles['labelCell']}>
              <Typography as="p" variant="mini-bold">
                {row.label}
              </Typography>
            </td>
            {products.map((product) => (
              <td key={product.id}>{row.render(product)}</td>
            ))}
          </tr>
        ))}
      </tbody>
      <tfoot className={styles['tfoot']}>
        <tr>
          <td className={styles['labelCell']} />
          {products.map((product) => {
            const countInCart = getCountInCart?.(product.id) ?? 0;
            return (
              <td key={product.id}>
                <Flex gap={8}>
                  <Button
                    variant="primary"
                    label={
                      countInCart > 0
                        ? `${addToCartLabel} (${countInCart})`
                        : addToCartLabel
                    }
                    aria-label={`Add ${product.name} to bag`}
                    onClick={() => onAddToCart(product)}
                    full
                  />
                  {!isMobile && (
                    <Button
                      variant="tertiary"
                      label="View"
                      onClick={() => onProductClick(product)}
                      to={product.url ?? undefined}
                      full
                    />
                  )}
                </Flex>
              </td>
            );
          })}
        </tr>
      </tfoot>
    </table>
  );
};
